import {
  FormState,
  FormStructure,
  FormValidatorsContext,
  FormValuesConstraint,
  HistoryEntry,
  NavigationControllerNodes,
  StructureProp,
} from '../types';

import {asGroupNodes, getStepAtPath} from './structure';
import {stepValidation} from './validation';

export const getNextHistoryEntry = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
  K,
>(
  state: FormState<T, S>,
  structure: StructureProp<T, S>,
  structurePaths: string[],
  /*
   * Callers pass a `NavigationControllers<T, S, K>`, but this must be declared
   * `unknown`. That type is a mapped type over `GetStepsWithNext`, a deeply
   * recursive conditional; making TypeScript relate it to the string-keyed view
   * used below — via either an assertion or an assignment — expands it into
   * millions of instantiations and OOMs `tsc`. An `unknown` parameter needs no
   * relation, and the assertion out of `unknown` is free.
   */
  navigationControllers: unknown,
  validatorsCtx: FormValidatorsContext<T, S, K>,
): HistoryEntry<T, S> | undefined => {
  const {navigation} = state;
  const currentStepIsValid = stepValidation(
    state,
    structure,
    String(navigation.currentStep),
  );
  const currentHistory = navigation.history[navigation.history.length - 1];
  const currentPathIndex = structurePaths.indexOf(currentHistory.path);
  const stepStructure = getStepAtPath<T>(structure, currentHistory.path);
  let nextGroup: string;

  // Only continue if current step is valid
  if (!currentStepIsValid) return undefined;

  // If bifurcation
  if (stepStructure?.next && Object.keys(stepStructure.next).length) {
    // `NavigationControllers` is keyed by the literal names of the steps that
    // have children; the lookup key here is only known as a string at runtime.
    const controllers = navigationControllers as NavigationControllerNodes<
      T,
      S,
      K
    >;
    const nextStepName =
      controllers?.[String(navigation.currentStep)]?.(validatorsCtx);
    const nextStepPath = `${currentHistory.path}.next.${nextStepName}`;

    // Grouped multi-step form
    if (asGroupNodes<T>(structure)) {
      nextGroup = nextStepPath.split('.')[0];
    }

    const newEntry: HistoryEntry<T, S> = {
      group: nextGroup as HistoryEntry<T, S>['group'],
      step: nextStepName as HistoryEntry<T, S>['step'],
      path: nextStepPath,
      skipped: false,
    };

    return newEntry;
  }

  // No bifurcation
  const hasParent = currentHistory.path.includes('next');
  let pathToFilter = `${currentHistory.path}.`;

  if (hasParent) {
    const indexOfNext = currentHistory.path.indexOf('.next');

    pathToFilter = currentHistory.path.slice(0, indexOfNext);
  }

  const nextPaths = structurePaths.filter((path, index) => {
    return index > currentPathIndex && !path.startsWith(pathToFilter);
  });

  if (nextPaths.length) {
    let nextStepPath = nextPaths[0];
    const nextPathParts = nextStepPath.split('.');
    let nextStep = nextPathParts[nextPathParts.length - 1];

    // Grouped multi-step form
    if (asGroupNodes<T>(structure) && nextPathParts.length <= 1) {
      nextStepPath = nextPaths[1];

      const nextPathParts2 = nextStepPath.split('.');

      nextGroup = nextPathParts2[0];
      nextStep = nextPathParts2[nextPathParts2.length - 1];
    } else if (asGroupNodes<T>(structure)) {
      nextGroup = nextPathParts[0];
    }

    const newEntry: HistoryEntry<T, S> = {
      group: nextGroup as HistoryEntry<T, S>['group'],
      step: nextStep as HistoryEntry<T, S>['step'],
      path: nextStepPath,
      skipped: false,
    };

    return newEntry;
  }

  return undefined;
};
